import React from 'react';
import {
  faPlus,
  faEdit,
  faTrash,
  faBuilding,
  faUsers,
  faCalendar,
  faSearch,
  faUserPlus,
  faUserInjured
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import {
  SAASOrganizationService,
  OrganizationWithUserCount,
  SAASCreateOrganizationData,
  SAASUpdateOrganizationData,
  OrganizationWithConsultasCount
} from '../services/saasOrganizationService';
import PageHeader from '../components/PageHeader';
import Input from '../components/Input';
import Button from '../components/Button';
import InviteUserModal from '../components/InviteUserModal';
import OrganizationFormModal, {
  EditOrganizationFormData
} from '../components/OrganizationFormModal';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Alert from '../components/Alert';
import IconButton from '../components/IconButton';

const OrganizationManagerPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<
    (OrganizationWithUserCount & OrganizationWithConsultasCount)[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationWithUserCount | null>(null);
  const [deleteOrgId, setDeleteOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async (): Promise<void> => {
    try {
      const result = await SAASOrganizationService.getAllOrganizations();

      if (result.success && Array.isArray(result.data)) {
        setOrganizations(result.data as OrganizationWithUserCount[]);
      } else {
        setError(result.message);
      }
    } catch (error: any) {
      setError('Erro ao carregar organizações');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (formData: EditOrganizationFormData): Promise<void> => {
    setError('');
    setSuccess('');

    const createData: SAASCreateOrganizationData = {
      name: formData.name,
      slug: formData.slug,
      slogan: formData.slogan,
      inviteEmail: formData.inviteEmail
    };

    try {
      const result = await SAASOrganizationService.createOrganization(createData);

      if (result.success) {
        setSuccess(result.message);
        setShowCreateModal(false);
        fetchOrganizations();
      } else {
        setError(result.message);
      }
    } catch (error: any) {
      setError('Erro ao criar organização: ' + error.message);
    }
  };

  const handleEditOrganization = async (formData: EditOrganizationFormData): Promise<void> => {
    setError('');
    setSuccess('');

    if (!selectedOrg) return;

    const updateData: SAASUpdateOrganizationData = {
      name: formData.name,
      slug: formData.slug,
      slogan: formData.slogan
    };

    try {
      const result = await SAASOrganizationService.updateOrganization(selectedOrg.id, updateData);

      if (result.success) {
        setSuccess(result.message);
        setShowEditModal(false);
        setSelectedOrg(null);
        fetchOrganizations();
      } else {
        setError(result.message);
      }
    } catch (error: any) {
      setError('Erro ao atualizar organização: ' + error.message);
    }
  };

  const handleDeleteOrganization = async (orgId: string): Promise<void> => {
    try {
      const result = await SAASOrganizationService.deleteOrganization(orgId);

      if (result.success) {
        setSuccess(result.message);
        setDeleteOrgId(null);
        fetchOrganizations();
      } else {
        setError(result.message);
        setDeleteOrgId(null);
      }
    } catch (error: any) {
      setError('Erro ao excluir organização: ' + error.message);
      setDeleteOrgId(null);
    }
  };

  const openEditModal = (org: OrganizationWithUserCount): void => {
    setSelectedOrg(org);
    setShowEditModal(true);
  };

  const openInviteModal = (org: OrganizationWithUserCount): void => {
    setSelectedOrg(org);
    setShowInviteModal(true);
  };

  const openCreateModal = (): void => {
    setShowCreateModal(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const filteredOrganizations = organizations.filter(
    org =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-6 flex-grow p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <PageHeader
          title="Gerenciador de Organizações"
          subtitle="Gerencie todas as organizações do sistema"
        />
        <Button onClick={openCreateModal} variant="primary" leftIcon={faPlus}>
          Nova Organização
        </Button>
      </div>

      {/* Alerts */}
      {error && <Alert variant="error" message={error} onClose={() => setError('')} />}
      {success && <Alert variant="success" message={success} onClose={() => setSuccess('')} />}

      {/* Search */}
      <Input
        placeholder="Buscar organizações..."
        value={searchTerm}
        onChange={handleSearchChange}
        leftIcon={faSearch}
      />

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredOrganizations.map(org => (
          <div key={org.id} className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faBuilding} className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 truncate">{org.name}</h3>
                  <p className="text-sm text-gray-500 truncate">{org.slogan}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <IconButton
                  onClick={() => openInviteModal(org)}
                  icon={faUserPlus}
                  variant="success"
                  tooltip="Convidar usuário"
                />
                <IconButton
                  onClick={() => openEditModal(org)}
                  icon={faEdit}
                  variant="primary"
                  tooltip="Editar organização"
                />
                <IconButton
                  onClick={() => setDeleteOrgId(org.id)}
                  icon={faTrash}
                  variant="danger"
                  tooltip="Excluir organização"
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm text-gray-500">
                <FontAwesomeIcon
                  icon={faUsers}
                  className={clsx('w-4 h-4 mr-2', {
                    'text-red-600': org.user_organizations?.[0]?.count === 0
                  })}
                />
                <span
                  className={clsx({
                    'text-red-600': org.user_organizations?.[0]?.count === 0
                  })}
                >
                  {org.user_organizations?.[0]?.count || 0} usuários
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <FontAwesomeIcon
                  icon={faUserInjured}
                  className={clsx('w-4 h-4 mr-2', {
                    'text-yellow-600': org.consultas?.[0]?.count === 0
                  })}
                />
                <span
                  className={clsx({
                    'text-yellow-600': org.consultas?.[0]?.count === 0
                  })}
                >
                  {org.consultas?.[0]?.count || 0} consultas
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2" />
                <span>
                  Criada em{' '}
                  {org.created_at
                    ? new Date(org.created_at).toLocaleDateString('pt-BR')
                    : 'Data não disponível'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOrganizations.length === 0 && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faBuilding} className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma organização encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente uma busca diferente.' : 'Comece criando uma nova organização.'}
          </p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <OrganizationFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateOrganization}
          mode="create"
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrg && (
        <OrganizationFormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          organization={selectedOrg}
          onSave={handleEditOrganization}
          mode="edit"
        />
      )}

      {/* Invite User Modal */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        organization={selectedOrg || { id: '', name: '' }}
        onInviteSuccess={() => {
          setSuccess('Convite enviado com sucesso!');
          fetchOrganizations();
        }}
      />

      {/* Confirmation Dialog */}
      {deleteOrgId && (
        <ConfirmationDialog
          title="Excluir Organização"
          message="Tem certeza que deseja excluir esta organização? Esta ação não pode ser desfeita."
          onConfirm={() => handleDeleteOrganization(deleteOrgId)}
          onCancel={() => setDeleteOrgId(null)}
          confirmText="Excluir"
          cancelText="Cancelar"
          type="danger"
        />
      )}
    </div>
  );
};

export default OrganizationManagerPage;
